import { useEffect, useState } from "react";
import {
  Bot,
  Copy,
  Send,
  Sparkles,
  Trash2,
  Wrench,
  X,
} from "lucide-react";
import type { AgentFlowNode } from "./AgentNode";
import type { WorkflowNodeData } from "../../shared/types";

interface InspectorProps {
  node: AgentFlowNode | null;
  onChange: (data: WorkflowNodeData) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onCopilotCommand: (command: string) => string;
  onClose: () => void;
}

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
}

export function Inspector({
  node,
  onChange,
  onDuplicate,
  onDelete,
  onCopilotCommand,
  onClose,
}: InspectorProps) {
  const [tab, setTab] = useState<"config" | "copilot">("config");
  const [command, setCommand] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: "assistant", text: "Posso alterar este grafo com comandos em linguagem natural." },
  ]);

  useEffect(() => {
    if (node) setTab("config");
  }, [node?.id]);

  function patch(data: Partial<WorkflowNodeData>) {
    if (!node) return;
    onChange({ ...node.data, ...data });
  }

  function sendCommand(text = command) {
    const value = text.trim();
    if (!value) return;
    const reply = onCopilotCommand(value);
    setMessages((current) => [
      ...current,
      { role: "user", text: value },
      { role: "assistant", text: reply },
    ]);
    setCommand("");
  }

  return (
    <aside className="inspector">
      <div className="inspector-tabs" role="tablist">
        <button className={tab === "config" ? "is-active" : ""} onClick={() => setTab("config")}>
          <Wrench size={14} />Configuração
        </button>
        <button className={tab === "copilot" ? "is-active" : ""} onClick={() => setTab("copilot")}>
          <Sparkles size={14} />Copiloto
        </button>
        <button
          className="inspector-close"
          aria-label="Fechar painel"
          title="Fechar painel"
          onClick={() => {
            setTab("config");
            onClose();
          }}
        >
          <X size={16} />
        </button>
      </div>

      {tab === "config" ? (
        node ? (
          <div className="inspector-scroll">
            <div className="inspector-heading">
              <span className={`inspector-kind inspector-kind--${node.data.kind}`}><Bot size={15} /></span>
              <div><strong>{node.data.label}</strong><small>{node.data.kind}</small></div>
              <label className="switch" title="Ativar ou desativar nó">
                <input
                  type="checkbox"
                  checked={node.data.active !== false}
                  onChange={(event) => patch({ active: event.target.checked })}
                />
                <span />
              </label>
            </div>

            <div className="form-section">
              <label><span>Nome</span><input value={node.data.label} onChange={(e) => patch({ label: e.target.value })} /></label>
              <label><span>Descrição</span><input value={node.data.description} onChange={(e) => patch({ description: e.target.value })} /></label>
            </div>

            {(node.data.kind === "agent" || node.data.kind === "orchestrator") && (
              <>
                <div className="form-section">
                  <label>
                    <span>Provedor</span>
                    <select value={node.data.provider ?? "demo"} onChange={(e) => patch({ provider: e.target.value as WorkflowNodeData["provider"] })}>
                      <option value="demo">Demo local</option>
                      <option value="openai-compatible">OpenAI compatível</option>
                      <option value="codex-cli">Codex CLI</option>
                      <option value="claude-cli">Claude CLI</option>
                      <option value="grok-cli">Grok CLI</option>
                      <option value="hermes-cli">Hermes</option>
                    </select>
                  </label>
                  <label>
                    <span>Política de modelo</span>
                    <select value={node.data.modelPolicy ?? "balanced"} onChange={(e) => patch({ modelPolicy: e.target.value as WorkflowNodeData["modelPolicy"] })}>
                      <option value="economical">Econômico</option>
                      <option value="balanced">Balanceado</option>
                      <option value="deep">Profundo</option>
                    </select>
                  </label>
                  <label>
                    <span>Em caso de erro</span>
                    <select value={node.data.onError ?? "stop"} onChange={(e) => patch({ onError: e.target.value as WorkflowNodeData["onError"] })}>
                      <option value="stop">Interromper workflow</option>
                      <option value="continue">Continuar com alerta</option>
                    </select>
                  </label>
                  {node.data.provider === "openai-compatible" && (
                    <label><span>Modelo</span><input value={node.data.model ?? ""} onChange={(e) => patch({ model: e.target.value })} placeholder="modelo-do-provedor" /></label>
                  )}
                  <label>
                    <span>Orçamento máximo</span>
                    <div className="input-with-suffix">
                      <input type="number" min="0" step="0.1" value={node.data.budget ?? 1} onChange={(e) => patch({ budget: Number(e.target.value) })} />
                      <span>R$</span>
                    </div>
                  </label>
                </div>
                <div className="form-section">
                  <label><span>Instruções</span><textarea rows={8} value={node.data.instructions ?? ""} onChange={(e) => patch({ instructions: e.target.value })} /></label>
                </div>
              </>
            )}

            <div className="inspector-actions">
              <button className="button button--secondary" onClick={onDuplicate}><Copy size={14} />Duplicar</button>
              <button className="button button--danger-ghost" onClick={onDelete}><Trash2 size={14} />Excluir</button>
            </div>
          </div>
        ) : (
          <div className="inspector-empty">
            <span><Wrench size={18} /></span>
            <strong>Selecione um nó</strong>
            <p>As propriedades e permissões aparecerão aqui.</p>
          </div>
        )
      ) : (
        <div className="copilot-panel">
          <div className="copilot-messages">
            {messages.map((message, index) => (
              <div className={`chat-message chat-message--${message.role}`} key={`${message.role}-${index}`}>
                {message.role === "assistant" && <Sparkles size={13} />}
                <p>{message.text}</p>
              </div>
            ))}
          </div>
          <div className="copilot-suggestions">
            <button onClick={() => sendCommand("Adicione um revisor antes da aprovação")}>Adicionar revisor</button>
            <button onClick={() => sendCommand("Use modelo econômico na pesquisa")}>Economizar pesquisa</button>
          </div>
          <div className="copilot-composer">
            <textarea
              value={command}
              onChange={(event) => setCommand(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  sendCommand();
                }
              }}
              placeholder="Descreva a alteração..."
              rows={3}
            />
            <button aria-label="Enviar comando" title="Enviar" onClick={() => sendCommand()}><Send size={15} /></button>
          </div>
        </div>
      )}
    </aside>
  );
}
